import React from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon } from "lucide-react";
import { DataInterval } from "../types";

interface DataIntervalConfig {
  value: DataInterval;
  labelKey: string;
  fallbackLabel: string;
}

const DATA_INTERVAL_OPTIONS: DataIntervalConfig[] = [
  {
    value: "daily",
    labelKey: "chart.dataInterval.daily",
    fallbackLabel: "Daily",
  },
  {
    value: "weekly",
    labelKey: "chart.dataInterval.weekly",
    fallbackLabel: "Weekly",
  },
  {
    value: "monthly",
    labelKey: "chart.dataInterval.monthly",
    fallbackLabel: "Monthly",
  },
] as const;

export interface DataIntervalSelectorProps {
  interval: DataInterval;
  onIntervalChange: (interval: DataInterval) => void;
  className?: string;
}

export const DataIntervalSelector: React.FC<DataIntervalSelectorProps> = ({
  interval,
  onIntervalChange,
  className,
}) => {
  const { t } = useTranslation();

  const currentOption = DATA_INTERVAL_OPTIONS.find(
    (opt) => opt.value === interval,
  );
  const displayLabel = currentOption
    ? t(currentOption.labelKey, currentOption.fallbackLabel)
    : interval.charAt(0).toUpperCase() + interval.slice(1);

  return (
    <DropdownMenu data-testid="data-interval-selector">
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className}
          aria-label={t(
            "chart.dataInterval.selectInterval",
            "Select data interval",
          )}
          data-testid="data-interval-button"
        >
          {displayLabel}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="data-interval-dropdown">
        {DATA_INTERVAL_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onClick={() => onIntervalChange(opt.value)}
            className="cursor-pointer"
            data-testid={`data-interval-option-${opt.value}`}
          >
            {t(opt.labelKey, opt.fallbackLabel)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
